/**
 * Admin → "Audit-loki" page. Read-only viewer for smt_audit_log entries.
 * Filters by action / target_type are exposed as a small search form so the
 * admin can drill into individual user/order/feedback histories.
 */
import { listAudit, type AuditEntry } from '../api/audit.ts';
import { currentUser } from '../state/session.ts';
import { t, getLang } from '../i18n/index.ts';
import { APIRequestError } from '../api/client.ts';

let filterAction = '';
let filterTargetType = '';

export async function renderAdminAudit(root: HTMLElement): Promise<void> {
  const u = currentUser();
  if (!u || u.role !== 'admin') {
    root.innerHTML = `<div class="simple-page"><div class="placeholder-card" style="color:rgb(140,55,55)">${escapeHtml(t('admin_only'))}</div></div>`;
    return;
  }

  root.innerHTML = `
    <div class="simple-page">
      <h1 class="simple-page-title">${escapeHtml(t('admin_audit_title'))}</h1>
      <div class="simple-page-sub">${escapeHtml(t('admin_audit_sub'))}</div>
      <div class="filter-bar">
        <div class="filter-group">
          <label class="filter-label">${escapeHtml(t('admin_audit_filter_action'))}</label>
          <input id="auditFilterAction" class="filter-input" value="${escapeHtml(filterAction)}" placeholder="user.approved, order.approved, …" />
        </div>
        <div class="filter-group">
          <label class="filter-label">${escapeHtml(t('admin_audit_filter_target'))}</label>
          <select id="auditFilterTarget" class="filter-input">
            <option value="">${escapeHtml(t('filter_all'))}</option>
            ${['user', 'estate', 'order', 'feedback', 'share'].map(
              (s) => `<option value="${s}" ${s === filterTargetType ? 'selected' : ''}>${s}</option>`,
            ).join('')}
          </select>
        </div>
        <button id="auditApply" class="filter-reset">${escapeHtml(t('admin_audit_apply'))}</button>
      </div>
      <div id="auditTable">${escapeHtml(t('view_admin_loading'))}</div>
    </div>
  `;

  document.getElementById('auditApply')!.addEventListener('click', () => {
    filterAction = (document.getElementById('auditFilterAction') as HTMLInputElement).value.trim();
    filterTargetType = (document.getElementById('auditFilterTarget') as HTMLSelectElement).value;
    void refreshTable(root);
  });

  await refreshTable(root);
}

async function refreshTable(root: HTMLElement): Promise<void> {
  const tbl = root.querySelector<HTMLDivElement>('#auditTable')!;
  tbl.textContent = t('view_admin_loading');
  try {
    const rows = await listAudit({
      action: filterAction || undefined,
      target_type: filterTargetType || undefined,
      limit: 200,
    });
    if (!rows.length) {
      tbl.innerHTML = `<div class="placeholder-card">${escapeHtml(t('admin_audit_empty'))}</div>`;
      return;
    }
    tbl.innerHTML = `
      <div class="listing-table-wrap"><table class="listing-table">
        <thead><tr>
          <th>${escapeHtml(t('admin_audit_th_ts'))}</th>
          <th>${escapeHtml(t('admin_audit_th_actor'))}</th>
          <th>${escapeHtml(t('admin_audit_th_action'))}</th>
          <th>${escapeHtml(t('admin_audit_th_target'))}</th>
          <th>${escapeHtml(t('admin_audit_th_message'))}</th>
          <th>${escapeHtml(t('admin_audit_th_details'))}</th>
        </tr></thead>
        <tbody>
          ${rows.map(renderRow).join('')}
        </tbody>
      </table></div>
    `;
  } catch (e) {
    tbl.innerHTML = `<div class="placeholder-card" style="color:rgb(140,55,55)">${escapeHtml(formatErr(e as Error))}</div>`;
  }
}

function renderRow(a: AuditEntry): string {
  const target = a.target_type ? `${a.target_type}#${a.target_id ?? '?'}` : '—';
  return `
    <tr>
      <td>${escapeHtml(formatTs(a.ts))}</td>
      <td>${escapeHtml(a.actor_label || (a.actor_user_id ? `user#${a.actor_user_id}` : 'system'))}</td>
      <td><code>${escapeHtml(a.action)}</code></td>
      <td><code>${escapeHtml(target)}</code></td>
      <td>${escapeHtml(a.message || '')}</td>
      <td><code style="font-size:11px;opacity:.8">${escapeHtml(a.details || '')}</code></td>
    </tr>`;
}

function formatTs(s: string): string {
  try {
    return new Date(s).toLocaleString(getLang() === 'en' ? 'en-GB' : 'fi-FI');
  } catch {
    return s;
  }
}
function formatErr(e: Error): string {
  if (e instanceof APIRequestError) return e.body.error || e.message;
  return e.message;
}
function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
}
